/**
 * Navigation for workshops with «الواجهة الجديدة للورشة» (user.workshopUiV2) — ONE source for all three nav
 * surfaces: the desktop SideRail and the legacy phone menu (components/Navbar.tsx), and the phone «المزيد» sheet /
 * top-bar title / bottom tabs (mobile/navModel.ts). Each of those calls this ONLY when isWorkshopV2(user); for every
 * other user (and a workshop with the switch off) they stay exactly as they were.
 *
 * Wave 2 (spec §2.2 + critique #13): الرئيسية · الطلبات (badge «بانتظارك» from /pulse) · طلب جديد (gold) ·
 * التحليلات (/insights) · المالية (/money — for PRIVATE it also holds the «العهدة» tab, so the separate custody item is
 * gone) · العملاء (/customers) · الفريق (/team — replaces التقييمات and المكافأة) · المساعد · كروت العمل, then the parts
 * catalog with its two sub-pages (when the workshop's catalog switch is on). The old pages (/my-cash, /feedbacks,
 * /bonus) still open by URL; they are just no longer in a v2 workshop's nav.
 *
 * Labels are `ws:` keys (workshop/i18n/common.*.json), so any t() — default namespace or not — resolves them.
 */
import type { IconName } from '../components/Icon';
import { UserRole } from '../types';
import type { User } from '../types';
import './i18n/common';

/** action = «بانتظارك» orders; overdue = INDEPENDENT money past 30 days (amber); lowRatings = new ≤ 3★ ratings. */
export type WsNavBadgeKind = 'action' | 'overdue' | 'lowRatings';

export interface WsNavItem {
  key: string;
  to: string;
  /** A `ws:`-prefixed i18n key. */
  labelKey: string;
  /** Fallback label (Arabic) when a key is missing. */
  fallback?: string;
  icon: IconName;
  /** A live count shown next to the item (from usePulse). */
  badge?: WsNavBadgeKind;
  /** The gold call to action («طلب جديد»). */
  primary?: boolean;
}

export interface WsNavGroup {
  key: string;
  /** A `ws:` key; null = the top block (no heading). */
  titleKey: string | null;
  items: WsNavItem[];
}

/** True for a WORKSHOP login whose workshop has the v2 switch on. */
export function isWorkshopV2(user: User | null | undefined): boolean {
  return !!user && user.role === UserRole.WORKSHOP && !!user.workshopUiV2;
}

const it = (key: string, to: string, icon: IconName, fallback: string, extra?: Partial<WsNavItem>): WsNavItem => ({
  key, to, icon, labelKey: `ws:common.nav.${key}`, fallback, ...extra,
});

/** The grouped navigation for a v2 workshop; [] for anyone else. */
export function workshopNav(user: User | null | undefined): WsNavGroup[] {
  if (!isWorkshopV2(user)) return [];
  const u = user as User;
  const main: WsNavItem[] = [
    it('home', '/', 'home', 'الرئيسية'),
    it('orders', '/orders', 'orders', 'الطلبات', { badge: 'action' }),
    it('newOrder', '/orders/create', 'plus', 'طلب جديد', { primary: true }),
    it('insights', '/insights', 'analytics', 'التحليلات'),
    // PRIVATE: collections + the cash custody tab; INDEPENDENT: outstanding, ageing, statement.
    it('money', '/money', 'money', 'المالية', { badge: 'overdue' }),
    it('customers', '/customers', 'customers', 'العملاء'),
    // Receptionists, ratings and the bonus in one place.
    it('team', '/team', 'users', 'الفريق', { badge: 'lowRatings' }),
    it('assistant', '/assistant', 'robot', 'المساعد'),
    it('jobCards', '/job-cards', 'clipboard', 'كروت العمل'),
  ];
  const groups: WsNavGroup[] = [{ key: 'main', titleKey: null, items: main }];
  if (u.partsCatalogEnabled !== false) {
    groups.push({
      key: 'catalog',
      titleKey: 'ws:common.nav.catalogGroup',
      items: [
        it('catalog', '/parts-catalog', 'book', 'كتالوج القطع'),
        it('fitment', '/parts-catalog/fitment', 'puzzle', 'توافق القطع'),
        it('crossMake', '/parts-catalog/cross-make', 'layers', 'مرادفات الماركات'),
      ],
    });
  }
  return groups;
}

/** The phone tab bar for a v2 workshop (+ «المزيد», added by BottomNav): home · orders (badge) · «＋» · assistant. */
export function workshopBottomTabs(user: User | null | undefined): WsNavItem[] {
  if (!isWorkshopV2(user)) return [];
  return [
    it('home', '/', 'home', 'الرئيسية'),
    it('orders', '/orders', 'orders', 'الطلبات', { badge: 'action' }),
    it('newOrder', '/orders/create', 'plus', 'طلب جديد', { primary: true }),
    it('assistant', '/assistant', 'robot', 'المساعد'),
  ];
}

/** Is `to` the current page? '/' only on '/', others on themselves and their sub-paths (not /orders/create for /orders). */
export function wsNavActive(to: string, pathname: string): boolean {
  if (to === '/') return pathname === '/';
  if (to === '/orders') return pathname === '/orders' || (/^\/orders\/[^/]+/.test(pathname) && !pathname.startsWith('/orders/create'));
  return pathname === to || pathname.startsWith(`${to}/`);
}
